import { Banner, DataTable, Tag } from '@boltenergy-be/design-system'
import styles from './TariffsLogbook.module.scss'
import { LogbookEntry, TARIFF_LOG_COLUMNS, TariffLogColumnKeys } from './constants.ts'
import { ENERGY_TYPES, getProductFromPriceListUrl, PRODUCT_CONFIG_OPTIONS, PRODUCTS_CONFIG } from '@boltenergy-be/shared'
import { useStoreSelector } from 'hooks/store.ts'
import { useGetLanguage } from 'hooks/use-get-language'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import Link from 'components/link/Link.tsx'
import { DataTableRow } from '@boltenergy-be/design-system/dist/components/data-table/types'
import { useMemo } from 'react'
import { DATE_FORMAT } from 'constants/constants.ts'
import { selectPriceLists } from 'store/contact/selectors.ts'
import { selectCurrentServiceContracts } from 'store/contact/slice.ts'
import { formatContractStatus } from 'utils/contracts.ts'

const TariffsLogbook = () => {
  // Redux
  const { electricity, gas } = useStoreSelector(selectCurrentServiceContracts)
  const priceListsBreakdown = useStoreSelector(selectPriceLists)

  // i18n
  const { t } = useTranslation(['tariffs', 'common'])
  const language = useGetLanguage()

  // Constants
  const elPricelists = priceListsBreakdown?.electricity
  const gasPricelists = priceListsBreakdown?.gas
  const combinedPriceLists = useMemo<LogbookEntry[]>(() => {
    const entries: LogbookEntry[] = []

    elPricelists?.future?.forEach((futurePriceList, index) => {
      entries.push({
        ...futurePriceList,
        id: crypto.randomUUID(),
        status: 'startingOn',
        energyType: ENERGY_TYPES.electricity,
        product: getProductFromPriceListUrl(futurePriceList.url || '')
      })

      if (gasPricelists?.future?.[index]) {
        entries.push({
          ...gasPricelists.future[index],
          id: crypto.randomUUID(),
          status: 'startingOn',
          energyType: ENERGY_TYPES.gas,
          product: getProductFromPriceListUrl(gasPricelists.future[index].url || '')
        })
      }
    })

    if (elPricelists?.current) {
      const currentContractStatus = formatContractStatus(electricity)
      entries.push({
        ...elPricelists.current,
        id: crypto.randomUUID(),
        status: currentContractStatus.key,
        energyType: ENERGY_TYPES.electricity,
        product: getProductFromPriceListUrl(elPricelists.current?.url || '')
      })
    }

    if (gas && gasPricelists?.current) {
      const currentContractStatus = formatContractStatus(gas)
      entries.push({
        ...gasPricelists.current,
        id: crypto.randomUUID(),
        status: currentContractStatus.key,
        energyType: ENERGY_TYPES.gas,
        product: getProductFromPriceListUrl(gasPricelists.current?.url || '')
      })
    }

    elPricelists?.previous?.forEach((oldPriceList, index) => {
      entries.push({
        ...oldPriceList,
        id: crypto.randomUUID(),
        status: 'terminatedSince',
        energyType: ENERGY_TYPES.electricity,
        product: getProductFromPriceListUrl(oldPriceList.url || '')
      })

      if (gasPricelists?.previous?.[index]) {
        entries.push({
          ...gasPricelists.previous[index],
          id: crypto.randomUUID(),
          status: 'terminatedSince',
          energyType: ENERGY_TYPES.gas,
          product: getProductFromPriceListUrl(gasPricelists.previous[index].url || '')
        })
      }
    })

    return entries.filter((entry) => !!entry.product)
  }, [elPricelists, electricity, gas, gasPricelists])
  const rows = useMemo<DataTableRow<TariffLogColumnKeys>[]>(
    () =>
      combinedPriceLists.map(
        (entry) =>
          ({
            id: entry.id,
            uuid: crypto.randomUUID(),
            entries: {
              [TARIFF_LOG_COLUMNS.product]: {
                header: t(($) => $.logbook.headers.product),
                rawData: entry.product,
                content: entry.product
                  ? PRODUCTS_CONFIG[entry.product][PRODUCT_CONFIG_OPTIONS.name][language].toLowerCase().startsWith('plenty')
                    ? PRODUCTS_CONFIG[entry.product][PRODUCT_CONFIG_OPTIONS.name][language]
                    : `Bolt ${PRODUCTS_CONFIG[entry.product][PRODUCT_CONFIG_OPTIONS.name][language]}`
                  : '—'
              },
              [TARIFF_LOG_COLUMNS.status]: {
                header: t(($) => $.logbook.headers.status),
                rawData: entry.status,
                content: (
                  <Tag type={entry.status === 'activeSince' ? 'positive' : entry.status === 'startingOn' ? 'soft' : 'neutral'} size="small">
                    {t(($) => $.overview.statuses[entry.status])}
                  </Tag>
                )
              },
              [TARIFF_LOG_COLUMNS.energyType]: {
                header: t(($) => $.logbook.headers.energyType),
                rawData: entry.energyType,
                content: t(($) => $[entry.energyType === ENERGY_TYPES.electricity ? 'electricity' : 'gas'], { ns: 'common' })
              },
              [TARIFF_LOG_COLUMNS.start]: {
                header: t(($) => $.logbook.headers.start),
                rawData: entry.fromDate,
                content: entry.fromDate ? dayjs(entry.fromDate).format(DATE_FORMAT) : '—'
              },
              [TARIFF_LOG_COLUMNS.end]: {
                header: t(($) => $.logbook.headers.end),
                rawData: 'toDate' in entry ? entry.toDate : null,
                content: 'toDate' in entry && entry.toDate ? dayjs(entry.toDate).format(DATE_FORMAT) : '—'
              },
              [TARIFF_LOG_COLUMNS.download]: {
                header: '',
                content: entry.url ? (
                  <Link
                    href={!entry.url.startsWith('https://') ? `https://${entry.url}` : entry.url}
                    as="a"
                    download
                    trailingIcon="download"
                    target="_blank"
                    className="text-sm weight-semibold"
                  >
                    {t(($) => $.tariffCard, {
                      ns: 'common'
                    })}
                  </Link>
                ) : (
                  '—'
                )
              }
            }
          }) satisfies DataTableRow<TariffLogColumnKeys>
      ),
    [language, combinedPriceLists, t]
  )

  return combinedPriceLists?.length ? (
    <DataTable className={styles.logbook} rows={rows} />
  ) : (
    <Banner title={t(($) => $.logbook.noneFound)} />
  )
}

export default TariffsLogbook
